/**
 * Header network selector — the user picks which chain they operate on.
 * Enabled chains are selectable; pending chains are greyed with "coming soon".
 * Selecting a network also prompts MetaMask to switch/add it when connected.
 */
import { useState, useRef, useEffect } from 'react';
import { useNetwork, ChainInfo } from '../contexts/NetworkContext';

async function switchMetaMask(chain: ChainInfo) {
  const eth = (window as any).ethereum;
  if (!eth) return;
  const hex = '0x' + chain.chainId.toString(16);
  try {
    await eth.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] });
  } catch (e: any) {
    if (e?.code === 4902) {
      await eth.request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: hex,
          chainName: chain.name,
          nativeCurrency: chain.nativeCurrency,
          rpcUrls: chain.rpcUrls,
          blockExplorerUrls: chain.explorer ? [chain.explorer] : [],
        }],
      });
    }
    // user rejection is fine — the app-level selection still applies
  }
}

export default function NetworkSelector() {
  const { chains, selected, setNetwork, loading } = useNetwork();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  if (loading || !selected) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-1.5">
        <span className="size-2 rounded-full bg-slate-500 animate-pulse"></span>
        <span className="text-xs font-bold text-white">…</span>
      </div>
    );
  }

  const pick = (c: ChainInfo) => {
    setNetwork(c.chainId);
    setOpen(false);
    switchMetaMask(c); // fire-and-forget; MetaMask optional
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        title="Choose network"
        className="flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-1.5 hover:bg-slate-800 transition-colors"
      >
        <span className={`size-2 rounded-full ${selected.testnet ? 'bg-amber-400' : 'bg-emerald-400'}`}></span>
        <span className="text-xs font-bold text-white">{selected.name}</span>
        <span className="material-symbols-outlined text-slate-400" style={{ fontSize: 14 }}>expand_more</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1.5 w-72 overflow-hidden rounded-xl border border-border-subtle bg-white shadow-xl">
          <div className="border-b border-border-subtle bg-slate-50 px-4 py-2.5">
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-text-faint">Network</p>
            <p className="mt-0.5 text-[11px] text-text-faint">Where your new DIDs and on-chain records live.</p>
          </div>
          {chains.map((c) => (
            <button
              key={c.chainId}
              disabled={!c.enabled}
              onClick={() => pick(c)}
              className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                c.enabled ? 'hover:bg-slate-50 cursor-pointer' : 'opacity-45 cursor-not-allowed'
              }`}
            >
              <span className={`size-2 shrink-0 rounded-full ${
                !c.enabled ? 'bg-slate-300' : c.testnet ? 'bg-amber-400' : 'bg-emerald-500'
              }`}></span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span className="text-sm font-bold text-text-main">{c.name}</span>
                  {c.recommended && <span className="rounded-full bg-indigo-50 px-1.5 text-[9px] font-bold uppercase text-indigo-600">recommended</span>}
                  {c.testnet && <span className="rounded-full bg-amber-50 px-1.5 text-[9px] font-bold uppercase text-amber-600">testnet</span>}
                </span>
                <span className="block text-[11px] text-text-faint">
                  Chain {c.chainId}{!c.enabled ? ' — coming soon' : ''}
                </span>
              </span>
              {selected.chainId === c.chainId && (
                <span className="material-symbols-outlined text-indigo-600" style={{ fontSize: 16 }}>check</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
